import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Command } from "lucide-react";

const TaskCommandPalette = ({
    isOpen,
    onClose,
    role,
    onAction,
    assignOptions = [],
}) => {
    const [query, setQuery] = useState("");
    const [activeIndex, setActiveIndex] = useState(0);
    const inputRef = useRef(null);

    const commands = useMemo(() => {
        const base = [
            { id: "focus-add", label: "Focus quick add", group: "Actions", keywords: "create new task add" },
            { id: "advanced", label: "Open advanced create", group: "Actions", keywords: "modal form" },
            { id: "filter-today", label: "Show due today", group: "Filters", keywords: "today due" },
            { id: "filter-overdue", label: "Show overdue", group: "Filters", keywords: "overdue late" },
            { id: "filter-active", label: "Show all active", group: "Filters", keywords: "active open" },
            { id: "export", label: "Export CSV", group: "Actions", keywords: "download export" },
        ];

        if (role === "admin") {
            base.push(
                { id: "view-board", label: "Board view", group: "Views", keywords: "kanban board columns" },
                { id: "view-list", label: "Task list view", group: "Views", keywords: "list" },
                { id: "view-assignments", label: "By assignee view", group: "Views", keywords: "staff team workload" },
                { id: "view-analytics", label: "Analytics view", group: "Views", keywords: "stats chart" },
                { id: "view-inbox", label: "Inbox / triage", group: "Views", keywords: "inbox unassigned overdue triage" },
                { id: "open-automations", label: "Open automations", group: "Actions", keywords: "rules ifttt escalate overdue" },
                { id: "inbox-unassigned", label: "Triage unassigned", group: "Filters", keywords: "unassigned inbox" },
                { id: "inbox-overdue", label: "Triage overdue", group: "Filters", keywords: "overdue inbox" },
                { id: "inbox-high", label: "Triage high priority", group: "Filters", keywords: "high inbox" },
                { id: "inbox-nodue", label: "Triage no due date", group: "Filters", keywords: "due missing inbox" },
                { id: "bulk-assign", label: "Start bulk assign", group: "Actions", keywords: "select multiple reassign" }
            );
        } else {
            base.push(
                { id: "view-board", label: "Board view", group: "Views", keywords: "kanban board columns" },
                { id: "view-list", label: "Task list view", group: "Views", keywords: "list" },
                { id: "view-today", label: "Due today", group: "Views", keywords: "today" },
                { id: "view-all", label: "All my tasks", group: "Views", keywords: "all tasks" }
            );
        }

        assignOptions.slice(0, 8).forEach((opt) => {
            base.push({
                id: `assign-${opt.value}`,
                label: `Filter: ${opt.label.split(" (")[0]}`,
                group: "Assignees",
                keywords: opt.label,
                meta: { assignTo: opt.value },
            });
        });

        return base;
    }, [role, assignOptions]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return commands;
        return commands.filter(
            (c) =>
                c.label.toLowerCase().includes(q) ||
                c.keywords?.toLowerCase().includes(q) ||
                c.group.toLowerCase().includes(q)
        );
    }, [commands, query]);

    useEffect(() => {
        if (isOpen) {
            setQuery("");
            setActiveIndex(0);
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [isOpen]);

    useEffect(() => {
        setActiveIndex(0);
    }, [query]);

    const runCommand = (cmd) => {
        onAction(cmd.id, cmd.meta);
        onClose();
    };

    const handleKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter" && filtered[activeIndex]) {
            e.preventDefault();
            runCommand(filtered[activeIndex]);
        } else if (e.key === "Escape") {
            onClose();
        }
    };

    const groups = [...new Set(filtered.map((c) => c.group))];

    let rowIndex = -1;

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] px-4 bg-black/50 backdrop-blur-sm"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ scale: 0.96, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.96, opacity: 0 }}
                        className="w-full max-w-lg rounded-2xl border border-line bg-surface shadow-2xl overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center gap-2 px-4 py-3 border-b border-line">
                            <Search size={18} className="text-ink-muted shrink-0" />
                            <input
                                ref={inputRef}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Search tasks actions…"
                                className="flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink-muted"
                            />
                            <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-line text-[10px] text-ink-muted">
                                <Command size={10} />K
                            </kbd>
                        </div>
                        <div className="max-h-72 overflow-y-auto py-2">
                            {filtered.length === 0 && (
                                <p className="px-4 py-6 text-sm text-ink-muted text-center">
                                    No matching commands
                                </p>
                            )}
                            {groups.map((group) => (
                                <div key={group}>
                                    <p className="px-4 py-1 text-[10px] uppercase tracking-wide font-semibold text-ink-muted">
                                        {group}
                                    </p>
                                    {filtered
                                        .filter((c) => c.group === group)
                                        .map((cmd) => {
                                            rowIndex += 1;
                                            const idx = rowIndex;
                                            return (
                                                <button
                                                    key={cmd.id}
                                                    type="button"
                                                    onClick={() => runCommand(cmd)}
                                                    className={`w-full text-left px-4 py-2.5 text-sm transition ${
                                                        idx === activeIndex
                                                            ? "bg-primary-soft text-primary"
                                                            : "text-ink hover:bg-surface-2"
                                                    }`}
                                                >
                                                    {cmd.label}
                                                </button>
                                            );
                                        })}
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default TaskCommandPalette;

export function useTaskCommandShortcut(onOpen) {
    useEffect(() => {
        const handler = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                onOpen();
            }
            if (e.key === "/" && !["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) {
                e.preventDefault();
                onOpen();
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onOpen]);
}
